import * as React from "react";

import {
  Drawer as SaltDrawer,
  DrawerCloseButton as SaltDrawerCloseButton,
  H2,
  Text,
  type DrawerProps as SaltDrawerProps,
} from "@/design-system/salt";
type DrawerPosition = NonNullable<SaltDrawerProps["position"]>;

interface DrawerAccessibilityContextValue {
  descriptionId: string;
  registerDescription: (id: string) => () => void;
  registerTitle: (id: string) => () => void;
  titleId: string;
}

const DrawerAccessibilityContext = React.createContext<DrawerAccessibilityContextValue | undefined>(
  undefined,
);

export interface DrawerProps extends Omit<
  SaltDrawerProps,
  "aria-describedby" | "aria-labelledby" | "onOpenChange" | "open" | "position"
> {
  "aria-describedby"?: string;
  "aria-labelledby"?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  position?: DrawerPosition;
  style?: React.CSSProperties;
}

/**
 * Controlled application drawer backed directly by Salt.
 *
 * Salt owns the portal, modal scrim, Escape/click-away dismissal, focus trap,
 * and focus return. The local seam supplies the accessible title/description
 * relationships and the standard close affordance used across product pages.
 */
const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      "aria-describedby": ariaDescribedBy,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      children,
      className,
      onOpenChange,
      open,
      position = "right",
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId().replace(/:/g, "");
    const generatedDescriptionId = `drawer-${generatedId}-description`;
    const generatedTitleId = `drawer-${generatedId}-title`;
    const [descriptionId, setDescriptionId] = React.useState<string>();
    const [titleId, setTitleId] = React.useState(generatedTitleId);

    const registerDescription = React.useCallback((id: string) => {
      setDescriptionId(id);
      return () => {
        setDescriptionId((currentId) => (currentId === id ? undefined : currentId));
      };
    }, []);

    const registerTitle = React.useCallback(
      (id: string) => {
        setTitleId(id);
        return () => {
          setTitleId((currentId) => (currentId === id ? generatedTitleId : currentId));
        };
      },
      [generatedTitleId],
    );

    const accessibilityContext = React.useMemo<DrawerAccessibilityContextValue>(
      () => ({
        descriptionId: generatedDescriptionId,
        registerDescription,
        registerTitle,
        titleId: generatedTitleId,
      }),
      [generatedDescriptionId, generatedTitleId, registerDescription, registerTitle],
    );

    // Salt keeps a closing drawer mounted for its exit animation. Unmount the
    // controlled seam as soon as the product state closes so the surrounding
    // application is no longer inert and focus returns without a 300ms dead zone.
    if (!open) return null;

    return (
      <DrawerAccessibilityContext.Provider value={accessibilityContext}>
        <SaltDrawer
          {...props}
          aria-describedby={ariaDescribedBy ?? descriptionId}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy ?? (ariaLabel === undefined ? titleId : undefined)}
          className={className}
          onOpenChange={onOpenChange}
          open={open}
          position={position}
          ref={ref}
          style={{
            width: position === "left" || position === "right" ? "75%" : "100%",
            maxWidth: position === "left" || position === "right" ? "24rem" : undefined,
            ...props.style,
          }}
        >
          {children}
          <SaltDrawerCloseButton onClick={() => onOpenChange(false)} />
        </SaltDrawer>
      </DrawerAccessibilityContext.Provider>
    );
  },
);
Drawer.displayName = "Drawer";

export interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const DrawerHeader = React.forwardRef<HTMLDivElement, DrawerHeaderProps>(
  ({ className, style, ...props }, ref) => (
    <div
      {...props}
      className={className}
      ref={ref}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--salt-spacing-100)",
        textAlign: "center",
        ...style,
      }}
    />
  ),
);
DrawerHeader.displayName = "DrawerHeader";

export interface DrawerFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const DrawerFooter = React.forwardRef<HTMLDivElement, DrawerFooterProps>(
  ({ className, style, ...props }, ref) => (
    <div
      {...props}
      className={className}
      ref={ref}
      style={{
        display: "flex",
        flexDirection: "column-reverse",
        justifyContent: "flex-end",
        gap: "var(--salt-spacing-100)",
        ...style,
      }}
    />
  ),
);
DrawerFooter.displayName = "DrawerFooter";

export interface DrawerTitleProps extends Omit<React.ComponentPropsWithoutRef<typeof H2>, "id"> {
  id?: string;
}

const DrawerTitle = React.forwardRef<HTMLHeadingElement, DrawerTitleProps>(
  ({ className, id, style, ...props }, ref) => {
    const context = React.useContext(DrawerAccessibilityContext);
    const fallbackId = React.useId().replace(/:/g, "");
    const resolvedId = id ?? context?.titleId ?? `drawer-title-${fallbackId}`;

    React.useEffect(() => context?.registerTitle(resolvedId), [context, resolvedId]);

    return (
      <H2
        {...props}
        className={className}
        id={resolvedId}
        ref={ref}
        style={style}
        styleAs="h3"
      />
    );
  },
);
DrawerTitle.displayName = "DrawerTitle";

export interface DrawerDescriptionProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Text>,
  "as" | "id"
> {
  id?: string;
  style?: React.CSSProperties;
}

const DrawerDescription = React.forwardRef<HTMLParagraphElement, DrawerDescriptionProps>(
  ({ className, id, ...props }, ref) => {
    const context = React.useContext(DrawerAccessibilityContext);
    const fallbackId = React.useId().replace(/:/g, "");
    const resolvedId = id ?? context?.descriptionId ?? `drawer-description-${fallbackId}`;

    React.useEffect(() => context?.registerDescription(resolvedId), [context, resolvedId]);

    return (
      <Text
        {...props}
        as="p"
        className={className}
        color="secondary"
        id={resolvedId}
        ref={ref}
        styleAs="label"
      />
    );
  },
);
DrawerDescription.displayName = "DrawerDescription";

export { Drawer, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle };
